import React, { useState, useRef } from "react";

const parseNumber = (text) => {
  const normalized = String(text).trim().replace(",", ".");
  if (normalized === "" || normalized === "." || normalized === "-") return NaN;
  return Number(normalized);
};

/**
 * Number input that is easy to overwrite:
 * - tapping selects the current value, so typing replaces it
 * - the box may be empty or partial ("0.") while typing; valid numbers update the parent live
 * - leaving it empty/invalid falls back to `fallback` (or stays empty with `allowEmpty`, reporting null)
 * - opens the numeric keypad on phones
 * - Enter commits and blurs, or calls `onEnter` when given (e.g. to jump to the next field)
 */
export function NumberField({
  value,
  onValueChange,
  min = 0,
  fallback = 0,
  decimals,
  allowEmpty = false,
  onEnter,
  className = "form-control",
  style,
  ...rest
}) {
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const selectOnNextPointerUp = useRef(false);

  const commit = (num) => {
    if (num === null) {
      onValueChange(null);
      return;
    }
    const rounded = decimals !== undefined ? Math.round(num * 10 ** decimals) / 10 ** decimals : num;
    onValueChange(rounded);
  };

  const handleFocus = (e) => {
    setDraft(value === undefined || value === null ? "" : String(value));
    setFocused(true);
    e.target.select();
    selectOnNextPointerUp.current = true; // mobile Safari/Chrome drop the selection on tap-up
  };

  const handlePointerUp = (e) => {
    if (selectOnNextPointerUp.current) {
      selectOnNextPointerUp.current = false;
      e.preventDefault();
      e.target.select();
    }
  };

  const handleChange = (e) => {
    const text = e.target.value.replace(/[^0-9.,]/g, "");
    setDraft(text);
    const num = parseNumber(text);
    if (!Number.isNaN(num) && num >= min) commit(num);
    else if (allowEmpty && text.trim() === "") commit(null);
  };

  const handleBlur = () => {
    const num = parseNumber(draft);
    if (Number.isNaN(num) || num < min) commit(allowEmpty && draft.trim() === "" ? null : fallback);
    setFocused(false);
  };

  return (
    <input
      {...rest}
      type="text"
      inputMode="decimal"
      pattern="[0-9]*[.,]?[0-9]*"
      autoComplete="off"
      className={className}
      style={style}
      value={focused ? draft : (value ?? "")}
      onFocus={handleFocus}
      onMouseUp={handlePointerUp}
      onTouchEnd={handlePointerUp}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (onEnter) onEnter(e);
          else e.currentTarget.blur();
        }
      }}
    />
  );
}

export default NumberField;
